import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Input, Select, Upload, Breadcrumb, Row, Col, Radio, Checkbox, Image } from 'antd';
import { UploadOutlined, InfoCircleOutlined, EyeOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { uploadService, UploadedFile } from '../services/upload';
import { tenantService, Tenant } from '../services/tenants';
import { showConfirmModal, showInfoModalWithCheckbox, getMessage } from '../components/PopupModal';
import { authService } from '../services/auth';
import api from '../services/api';
import type { UploadFile, UploadProps } from 'antd';
import TenantPreviewModal from '../components/TenantPreviewModal';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const TenantIntroAddDetail: React.FC = () => {
  const navigate = useNavigate();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const [form] = Form.useForm();
  const [isEventContent, setIsEventContent] = useState(false);
  const [attachedFileList, setAttachedFileList] = useState<UploadFile[]>([]);
  const [additionalFileList, setAdditionalFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedAttachedFile, setUploadedAttachedFile] = useState<UploadedFile | null>(null);
  const [uploadedAdditionalFile, setUploadedAdditionalFile] = useState<UploadedFile | null>(null);
  const [layoutOptions, setLayoutOptions] = useState<string[]>(['layout1', 'layout2']);
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [phoneError, setPhoneError] = useState<string>('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [isModified, setIsModified] = useState(false);

  useNavigationBlocker(isModified, '/tenant-intro/add');

  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.role === 'super_admin';

  // useEffect to load layout options and company names
  useEffect(() => {
    const loadLayoutOptions = async () => {
      try {
        const response = await tenantService.getLayouts();
        setLayoutOptions(response.data || ['layout1', 'layout2']);
      } catch (error) {
        console.warn('Failed to fetch layouts, using default options:', error);
        setLayoutOptions(['layout1', 'layout2']);
      }
    };

    const loadCompanyNames = async () => {
      try {
        const response = await api.get('/tenant-units');
        const data = response.data;

        if (data.success && data.data) {
          // 모든 층의 rooms에서 companyName 추출
          const companyNames = new Set<string>();
          data.data.forEach((floor: any) => {
            floor.rooms.forEach((room: any) => {
              if (room.companyName && room.companyName.trim() !== '') {
                companyNames.add(room.companyName);
              }
            });
          });

          const options = Array.from(companyNames)
            .sort()
            .map(name => ({
              value: name,
              label: name,
            }));

          setCompanyOptions(options);
        }
      } catch (error) {
        console.error('Failed to load company names:', error);
      }
    };

    loadLayoutOptions();
    loadCompanyNames();
  }, []);

  // Layout 표시명 변환 함수
  const getLayoutDisplayName = (layout: string) => {
    switch (layout) {
      case 'layout1':
        return '레이아웃 1 (5개 구성)';
      case 'layout2':
        return '레이아웃 2 (4개 구성)';
      default:
        return layout;
    }
  };

  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          // navigate 전에 isModified를 false로 설정하여 useNavigationBlocker를 비활성화
          setIsModified(false);
          // setTimeout으로 상태 업데이트가 완료된 후 네비게이션 실행
          setTimeout(() => {
            navigate('/tenant-intro');
          }, 0);
        },
      });
    } else {
      navigate('/tenant-intro');
    }
  };

  // 대표번호 검증 핸들러
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // '-' 포함 여부 확인
    if (value.includes('-')) {
      setPhoneError("'-'를 제외하고 입력해주세요.");
    } else if (value && !/^\d*$/.test(value)) {
      // 숫자가 아닌 문자 확인
      setPhoneError('숫자만 입력 가능합니다.');
    } else {
      setPhoneError('');
    }

    // 폼 값 업데이트
    form.setFieldsValue({ phoneNumber: value });
  };

  // 입주사 설명 변경 핸들러 (Enter 키로만 줄바꿈)
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    // 최대 3줄로 제한
    const lines = value.split('\n');

    if (lines.length > 3) {
      // 3줄 초과 시 마지막 줄 제거
      const limitedLines = lines.slice(0, 3);
      const limitedValue = limitedLines.join('\n');
      form.setFieldsValue({ content: limitedValue });
      getMessage().warning('최대 3줄까지만 입력 가능합니다.');
    } else {
      // 사용자가 입력한 값 그대로 저장 (Enter로 입력한 \n 포함)
      form.setFieldsValue({ content: value });
    }
  };

  // 첨부파일 업로드 처리
  const handleAttachedFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);
      const validation = uploadService.validateFile(file as File);

      if (!validation.valid) {
        getMessage().error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }

      const response = await uploadService.uploadSingleImage(file as File);

      if (response.success) {
        setUploadedAttachedFile(response.data);
        getMessage().success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      getMessage().error('파일 업로드에 실패했습니다.');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // 추가 첨부파일 업로드 처리
  const handleAdditionalFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);
      const validation = uploadService.validateFile(file as File);

      if (!validation.valid) {
        getMessage().error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }

      const response = await uploadService.uploadSingleImage(file as File);

      if (response.success) {
        setUploadedAdditionalFile(response.data);
        getMessage().success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      getMessage().error('파일 업로드에 실패했습니다.');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleAttachedFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setAttachedFileList(fileList);
  };

  const handleAdditionalFileChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setAdditionalFileList(fileList);
  };

  const handleRemoveAttachedFile = () => {
    setAttachedFileList([]);
    setUploadedAttachedFile(null);
  };

  const handleRemoveAdditionalFile = () => {
    setAdditionalFileList([]);
    setUploadedAdditionalFile(null);
  };

  // 이벤트 콘텐츠 체크박스 변경 핸들러
  const handleEventContentChange = (checked: boolean) => {
    if (checked) {
      // 체크하는 경우 - 안내 모달 표시 (다시 보지 않기 옵션)
      const hideEventContentInfo = localStorage.getItem('hideEventContentInfo');

      if (hideEventContentInfo === 'true') {
        // 다시 보지 않기를 선택한 경우 바로 체크
        setIsEventContent(true);
      } else {
        // 안내 모달 표시
        showInfoModalWithCheckbox({
          title: '안내',
          content: '입주사 이벤트 콘텐츠 추가 시 승인요청 후\nHNINE 문의 바랍니다.\n02-0000-0000',
          onConfirm: () => {
            setIsEventContent(true);
          },
          onDontShowAgain: (dontShow) => {
            if (dontShow) {
              localStorage.setItem('hideEventContentInfo', 'true');
            }
          },
        });
      }
    } else {
      // 체크 해제하는 경우 - 파일이 업로드된 경우 확인
      if (uploadedAdditionalFile) {
        showConfirmModal({
          title: '알림',
          content: '첨부한 파일이 삭제됩니다.\n계속 진행하시겠습니까?',
          okText: '확인',
          cancelText: '취소',
          onConfirm: () => {
            // 확인을 누르면 파일 삭제 및 체크 해제
            handleRemoveAdditionalFile();
            setIsEventContent(false);
          },
          onCancel: () => {
            // 취소를 누르면 아무것도 하지 않음 (체크 상태 유지)
          },
        });
      } else {
        // 파일이 없는 경우 바로 체크 해제
        setIsEventContent(false);
      }
    }
  };

  const handlePreview = () => {
    try {
      const values = form.getFieldsValue();

      if (!values.companyName) {
        getMessage().warning('입주사명을 입력해주세요.');
        return;
      }

      if (!uploadedAttachedFile) {
        getMessage().warning('입주사 이미지를 첨부해주세요.');
        return;
      }

      setPreviewModalVisible(true);
    } catch (error) {
      console.warn('Form not yet initialized:', error);
    }
  };

  const getPreviewTenantData = (): Tenant => {
    const values = form.getFieldsValue();
    const description = values.content || '';

    return {
      id: 0,
      name: values.companyName || '입주사명',
      phone: values.phoneNumber || '',
      description: description,
      imageUrl: uploadedAttachedFile?.url || '',
      thumbnailUrl: isEventContent && uploadedAdditionalFile ? uploadedAdditionalFile.url : undefined,
      isEventContent: isEventContent,
      layout: (values.layout || 'layout2') as 'layout1' | 'layout2',
      approvalStatus: (isSuperAdmin ? 'approved' : 'pending') as 'pending' | 'approved' | 'rejected',
      status: (values.status || 'active') as 'active' | 'inactive' | 'closed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // tenantUnits를 제거하여 모달에서 선택한 회사명으로 API 조회하도록 함
    };
  };

  const handleNext = async () => {
    try {
      // 대표번호 에러 체크
      if (phoneError) {
        getMessage().error(phoneError);
        return;
      }

      // 폼 검증
      const values = await form.validateFields();

      // 필수 필드 검증
      if (!values.layout) {
        getMessage().error('레이아웃을 선택해주세요.');
        return;
      }

      if (!values.companyName) {
        getMessage().error('입주사명을 입력해주세요.');
        return;
      }

      // 입주사명이 유효한 옵션인지 검증
      const isValidCompany = companyOptions.some(option => option.value === values.companyName);
      if (!isValidCompany) {
        getMessage().error('입주사 관리에 등록된 회사명을 선택해주세요.');
        return;
      }

      if (!values.content) {
        getMessage().error('입주사 설명을 입력해주세요.');
        return;
      }

      if (!uploadedAttachedFile) {
        getMessage().error('입주사 이미지를 첨부해주세요.');
        return;
      }

      // 이벤트 콘텐츠인 경우 추가 첨부파일 필수
      if (isEventContent && !uploadedAdditionalFile) {
        getMessage().error('이벤트 콘텐츠를 체크하셨습니다. 이벤트 콘텐츠 첨부파일을 업로드해주세요.');
        return;
      }

      // Super Admin인 경우 확인 팝업 표시
      if (isSuperAdmin) {
        showConfirmModal({
          title: '알림',
          content: '저장 시 콘텐츠가 자동 승인됩니다.\n저장하시겠습니까?',
          okText: '확인',
          cancelText: '취소',
          onConfirm: () => performSave(values),
        });
      } else {
        // 일반 Admin인 경우 바로 저장
        await performSave(values);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        getMessage().error('필수 입력 항목을 확인해주세요.');
      } else {
        console.error('Validation error:', error);
      }
    }
  };

  const performSave = async (values: any) => {
    try {
      setSubmitting(true);

      // 입주사 등록 데이터 구성
      const tenantData = {
        name: values.companyName,
        phone: values.phoneNumber || '',
        description: values.content,
        imageUrl: uploadedAttachedFile!.url,
        thumbnailUrl: isEventContent && uploadedAdditionalFile ? uploadedAdditionalFile.url : undefined,
        isEventContent: isEventContent,
        layout: values.layout || 'layout2', // 폼에서 선택된 레이아웃 사용
        status: values.status || 'active',
        approvalStatus: (isSuperAdmin ? 'approved' : 'pending') as 'approved' | 'pending',
      };

      // 입주사 등록 API 호출
      const response = await tenantService.createTenant(tenantData);

      if (response.success) {
        // Super Admin: 자동 승인 완료 메시지
        // 일반 Admin: 승인 요청 완료 메시지
        const successMessage = isSuperAdmin
          ? '성공적으로 추가되었습니다.'
          : '승인 요청이 완료되었습니다.';
        getMessage().success(successMessage);

        // 모든 네비게이션 가드 강제 해제 후 이동
        clearAllBlockers();
        navigate('/tenant-intro');
      } else {
        getMessage().error('입주사 등록에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Failed to create tenant:', error);
      if (error.response?.data?.message) {
        getMessage().error(error.response.data.message);
      } else {
        getMessage().error('입주사 등록 중 오류가 발생했습니다.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '건물 안내' },
          { title: '입주사 소개', href: '/tenant-intro' },
          { title: '입주사 소개 추가' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          입주사 소개 추가
        </Title>
        <Button onClick={handlePreview}>미리보기</Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        style={{ width: '100%' }}
        initialValues={{
          layout: 'layout2',
          status: 'active',
          phoneNumber: '',
          approvalStatus: 'pending'
        }}
      
        onValuesChange={() => setIsModified(true)}
      >
        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[12, 8]}>
            <Col span={6}>
              <Form.Item
                label="레이아웃"
                name="layout"
              >
                <Select placeholder="레이아웃을 선택하세요" disabled>
                  <Option key="-" value="layout2">-</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[12, 8]} style={{ height: '54px' }}>
            <Col span={6}>
              <Form.Item
                label={<span>노출여부 <span style={{ color: 'red' }}>*</span></span>}
                name="status"
              >
                <Radio.Group>
                  <Radio value="active">노출</Radio>
                  <Radio value="inactive">비노출</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 입주사 정보 */}
        <Card title="입주사 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]} style={{ alignItems: 'flex-end', paddingBottom: '20px' }}>
            <Col span={6}>
              <Form.Item
                label={<span>입주사명 <span style={{ color: 'red' }}>*</span></span>}
                name="companyName"
                required={false}
                rules={[{ required: true, message: '입주사명을 입력해주세요.' }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  showSearch
                  placeholder="회사명을 입력하세요."
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={companyOptions}
                  style={{ width: '100%' }}
                  notFoundContent="등록된 입주사가 없습니다."
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                  입주사 관리에 등록된 입주사명만 검색됩니다.
                </Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={{ alignItems: 'flex-end' }}>
            <Col span={6} >
              <Form.Item
                label="대표번호"
                name="phoneNumber"
                validateStatus={phoneError ? 'error' : ''}
                help={phoneError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{phoneError}</span> : ''}
                style={{ marginBottom: 0 }}
              >
                <Input onChange={handlePhoneChange} />
              </Form.Item>
            </Col>
            <Col span={16}>
              {!phoneError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    '-' 를 제외하고 입력해주세요.
                  </Text>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 입주사 설명 */}
        <Card title="입주사 설명" style={{ marginBottom: '24px' }}>
          <Form.Item
            label={<span>내용 <span style={{ color: 'red' }}>*</span></span>}
            name="content"
            required={false}
            rules={[{ required: true, message: '입주사 설명을 입력해주세요.' }, { max: 123, message: '입주사 설명은 123자 이내로 입력해주세요.' }]}
          >
            <TextArea
              rows={3}
              placeholder="내용을 입력하세요."
              showCount
              maxLength={123}
              onKeyDown={(e) => {
                const value = (e.target as HTMLTextAreaElement).value;
                const lineCount = (value.match(/\n/g) || []).length;
                // Enter 키를 누르고 이미 2개의 줄바꿈이 있으면 (총 3줄) 입력 방지
                if (e.key === 'Enter' && lineCount >= 2) {
                  e.preventDefault();
                  getMessage().warning('최대 3줄까지만 입력 가능합니다.');
                }
              }}
              onChange={handleDescriptionChange}
              style={{
                maxWidth: '58ch',
                resize: 'none',
                whiteSpace: 'pre-wrap' // Enter 키로 입력한 줄바꿈 문자(\n) 유지
              }}
            />
          </Form.Item>
        </Card>

        {/* 입주사 이미지 */}
        <Card title="입주사 이미지" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ display: 'block', marginBottom: '8px', color: '#ffffff' }}>
              <span>첨부파일 <span style={{ color: 'red' }}>*</span></span>
            </Text>

            {/* 업로드된 이미지 미리보기 */}
            {uploadedAttachedFile && (
              <div style={{ marginBottom: '16px', border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Text style={{ color: '#ffffff' }}>업로드된 파일</Text>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveAttachedFile}
                    style={{ color: '#ff4d4f' }}
                  >
                    삭제
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <Image
                    width={100}
                    height={100}
                    src={uploadedAttachedFile.url}
                    alt="업로드된 이미지"
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                    preview={{
                      mask: <div><EyeOutlined /> 미리보기</div>
                    }}
                  />
                  <div>
                    <Text style={{ color: '#ffffff', display: 'block' }}>{uploadedAttachedFile.originalName}</Text>
                    <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                      크기: {uploadService.formatFileSize(uploadedAttachedFile.size)}
                    </Text>
                  </div>
                </div>
              </div>
            )}

            <Dragger
              fileList={attachedFileList}
              onChange={handleAttachedFileChange}
              customRequest={handleAttachedFileUpload}
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              disabled={uploading || !!uploadedAttachedFile}
              style={{
                background: '#141414',
                border: '2px dashed #303030',
                borderRadius: '8px'
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
              </p>
              <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                Upload
              </p>
            </Dragger>
          </div>
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: '#F24822',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#ffffff'
              }}>
                !
              </div>
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                파일형식: PNG, JPEG, JPG / 5MB 이하 (최대 1개)
              </Text>
            </div>
            <div style={{ marginLeft: '24px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                권장 이미지 사이즈: 0000px(가로) × 0000px(세로)
              </Text>
            </div>
          </div>
        </Card>


        {/* 승인 정보 */}
        {/* <Card title="승인 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                label="승인여부"
                name="approvalStatus"
                initialValue="pending"
              >
                <Select disabled style={{ backgroundColor: '#2a2a2a' }}>
                  <Option value="pending">대기중</Option>
                  <Option value="approved">승인</Option>
                  <Option value="rejected">반려</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="승인일"
                name="approvalDate"
              >
                <Input placeholder="승인 후 자동 입력" disabled style={{ backgroundColor: '#2a2a2a' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card> */}

        {/* 기타 정보 */}
        <Card title="기타 정보" style={{ marginBottom: '32px' }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <Checkbox
                  checked={isEventContent}
                  onChange={(e) => handleEventContentChange(e.target.checked)}
                >
                  이벤트 콘텐츠
                </Checkbox>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    입주사 관련 이벤트가 있는 경우 승인요청 후 <span style={{ color: '#F24822' }}>HNINE 문의</span> 바랍니다.
                    <span style={{ color: '#F24822' }}> 02-521-0518</span>
                  </Text>
                </div>
              </div>
              {isEventContent && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <Text style={{ display: 'block', marginBottom: '8px', color: '#ffffff' }}>
                      <span>첨부파일 <span style={{ color: 'red' }}>*</span></span>
                    </Text>

                    {/* 업로드된 이미지 미리보기 */}
                    {uploadedAdditionalFile && (
                      <div style={{ marginBottom: '16px', border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <Text style={{ color: '#ffffff' }}>업로드된 파일</Text>
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveAdditionalFile}
                            style={{ color: '#ff4d4f' }}
                          >
                            삭제
                          </Button>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <Image
                            width={100}
                            height={100}
                            src={uploadedAdditionalFile.url}
                            alt="업로드된 이미지"
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            preview={{
                              mask: <div><EyeOutlined /> 미리보기</div>
                            }}
                          />
                          <div>
                            <Text style={{ color: '#ffffff', display: 'block' }}>{uploadedAdditionalFile.originalName}</Text>
                            <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                              크기: {uploadService.formatFileSize(uploadedAdditionalFile.size)}
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}

                    <Dragger
                      fileList={additionalFileList}
                      onChange={handleAdditionalFileChange}
                      customRequest={handleAdditionalFileUpload}
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      disabled={uploading || !!uploadedAdditionalFile}
                      style={{
                        background: '#141414',
                        border: '2px dashed #303030',
                        borderRadius: '8px'
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
                      </p>
                      <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                        Upload
                      </p>
                    </Dragger>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: '#F24822',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        color: '#ffffff'
                      }}>
                        !
                      </div>
                      <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                        파일형식: PNG, JPEG, JPG / 5MB 이하 (최대 1개)
                      </Text>
                    </div>
                    <div style={{ marginLeft: '24px' }}>
                      <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                        권장 이미지 사이즈: 0000px(가로) × 0000px(세로)
                      </Text>
                    </div>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <Button size="large" onClick={handleCancel} disabled={submitting}>
            취소
          </Button>
          <Button type="primary" size="large" onClick={handleNext} loading={submitting}>
            {isSuperAdmin ? '추가' : '승인 요청'}
          </Button>
        </div>
      </Form>

      <TenantPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        tenant={previewModalVisible ? getPreviewTenantData() : null}
      />
    </Space>
  );
};

export default TenantIntroAddDetail;